import { useEffect, useRef } from 'react';
import { handleMonitoringFrontendError } from '@/shared/api';
import { FRONT_END_ERRORS, KEY_RECOIL, TAB_KEY } from '@/shared/config';
import {
  selectActiveTab,
  selectSelectedColumnInfo,
  selectSelectedStrip,
  useReportStore,
} from '@/entities/report';
import { auth } from '@/shared/lib';
import { useUpdateEffect } from '@/shared/lib/customHooks';
import { CHECK_CHANGE_ARTIFACT_THUMBNAIL_INTERVAL } from './artifactAreasTimeTracking.c';

export type ArtifactAreasTimeTrackingProps = {
  activeTab: string;
  profileId: string;
  studyFid: string | number;
  reportType: string;
  reportId: string | number;
  // passed by aiReport.tsx but never read by this component - pre-existing dead prop.
};

const ArtifactAreasTimeTracking = ({
  activeTab,
  profileId,
  studyFid,
  reportType,
  reportId,
}: ArtifactAreasTimeTrackingProps): null => {
  const activeButtonBeats = useReportStore(selectActiveTab(KEY_RECOIL.BEAT_HR));
  const selectedStrip = useReportStore(selectSelectedStrip(KEY_RECOIL.BEAT_HR));
  const selectedColumnInfo = useReportStore(selectSelectedColumnInfo(KEY_RECOIL.BEAT_HR));

  const hasChangedSelectedStrip = useRef(false);
  const hasChangedHr = useRef(false);
  const prevPos = useRef<{ movementX: number; movementY: number } | null>(null);
  const maxMovementX = useRef(0);
  const maxMovementY = useRef(0);

  /**
   * Records the largest pointer movement seen while the report tab is visible, feeding the artifact-area activity tracker. On a mousemove it compares the current movementX and movementY against the previously stored pair and either raises the recorded maxima or stores the new pair as the baseline, doing nothing while the document is hidden.
   *
   * @param e - DOM event from the mouse or key listeners; only events of type mousemove are measured.
   * @returns Nothing.
   */
  const handleMouseAndKey = (e: Event) => {
    if (document.visibilityState === 'visible') {
      if (e.type === 'mousemove') {
        const mouseEvent = e as MouseEvent;
        if (prevPos.current) {
          if (
            mouseEvent.movementX > prevPos.current.movementX ||
            mouseEvent.movementY > prevPos.current.movementY ||
            Math.abs(mouseEvent.movementX) > Math.abs(prevPos.current.movementX) ||
            Math.abs(mouseEvent.movementY) > Math.abs(prevPos.current.movementY)
          ) {
            maxMovementX.current = Math.max(maxMovementX.current, Math.abs(mouseEvent.movementX));
            maxMovementY.current = Math.max(maxMovementY.current, Math.abs(mouseEvent.movementY));
            return;
          }
        }
        prevPos.current = {
          movementX: mouseEvent.movementX,
          movementY: mouseEvent.movementY,
        };
      }
    }
  };

  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null = setInterval(() => {
      if (activeTab === TAB_KEY.BEATS && activeButtonBeats === 'ARTIFACT') {
        if (hasChangedSelectedStrip.current) {
          hasChangedSelectedStrip.current = false;
          return;
        }

        if (hasChangedHr.current) {
          hasChangedHr.current = false;
          return;
        }

        handleMonitoringFrontendError({
          exception: JSON.stringify({
            name: 'NoChangeArtifactThumbnailDetected',
            message: 'No change in artifact thumbnail detected',
            maxMovementX: maxMovementX.current,
            maxMovementY: maxMovementY.current,
            studyFid,
            profileId,
            reportType,
            reportId: reportType === 'Interim' ? reportId : undefined,
          }),
          type: FRONT_END_ERRORS.NO_CHANGE_ARTIFACT_THUMBNAIL_DETECTED,
          userId: auth.userId(),
        });

        maxMovementX.current = 0;
        maxMovementY.current = 0;
      }
    }, CHECK_CHANGE_ARTIFACT_THUMBNAIL_INTERVAL);
    return () => {
      clearInterval(interval);
      interval = null;
    };
  }, [activeTab, activeButtonBeats]);

  useUpdateEffect(() => {
    if (selectedStrip) {
      if (!hasChangedSelectedStrip.current) {
        hasChangedSelectedStrip.current = true;
      }
    }
  }, [selectedStrip]);

  useUpdateEffect(() => {
    if (selectedColumnInfo) {
      if (!hasChangedHr.current) {
        hasChangedHr.current = true;
      }
    }
  }, [selectedColumnInfo]);

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseAndKey);
    window.addEventListener('keydown', handleMouseAndKey);
    window.addEventListener('scroll', handleMouseAndKey);
    return () => {
      window.removeEventListener('mousemove', handleMouseAndKey);
      window.removeEventListener('keydown', handleMouseAndKey);
      window.removeEventListener('scroll', handleMouseAndKey);
    };
  }, [activeTab]);

  return null;
};

export default ArtifactAreasTimeTracking;
